import { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, Settings } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_PUBLIC_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';
import { PageShell } from './layout/PageShell';

interface UnsubscribePageProps {
  onNavigate: (page: 'home' | 'settings') => void;
}

export function UnsubscribePage({ onNavigate }: UnsubscribePageProps) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'invalid'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAndroid, setIsAndroid] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(56);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof navigator === 'undefined') return;
    const android = !document.documentElement.classList.contains('ios-pwa') &&
      (document.documentElement.classList.contains('android-pwa') || /android/i.test(navigator.userAgent));
    setIsAndroid(!!android);
    if (android) {
      const el = document.querySelector('.pwa-nav-top');
      const h = el ? (el as HTMLElement).getBoundingClientRect().height : 56;
      setHeaderHeight(Math.round(h));
    }
  }, []);

  useEffect(() => {
    handleUnsubscribe();
  }, []);

  useEffect(() => {
    if (isAndroid) return;
    window.scrollTo(0, 0);
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'instant' });
  }, [isAndroid]);

  const handleUnsubscribe = async () => {
    // Get token from URL hash
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.split('?')[1] || '');
    const token = params.get('token');

    if (!token) {
      setStatus('invalid');
      setErrorMessage('Ugyldig avmeldingslink. Token mangler.');
      return;
    }

    try {
      const response = await fetch(
        `${API_PUBLIC_BASE}/unsubscribe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        }
      );

      if (response.ok) {
        setStatus('success');
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.error || 'Kunne ikke melde deg av');
      }
    } catch (error) {
      devError('Unsubscribe error:', error);
      setStatus('error');
      setErrorMessage('Noe gikk galt. Prøv igjen senere.');
    }
  };

  const handleResubscribe = () => {
    onNavigate('settings');
  };

  const cardContent = (
    <div className="max-w-md w-full bg-white rounded-none shadow-none xl:rounded-2xl xl:shadow-xl p-8">
        {/* Loading state */}
        {status === 'loading' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
              <Loader size="lg" className="text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Behandler forespørsel...
            </h1>
            <p className="text-gray-600">
              Vennligst vent
            </p>
          </div>
        )}

        {/* Success state */}
        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Du er avmeldt!
            </h1>
            <p className="text-gray-600 mb-8">
              Du vil ikke lenger motta markedsførings-e-poster fra Talentfunn.
            </p>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-purple-900 mb-2">
                <strong>Viktig:</strong> Du vil fortsatt motta viktige e-poster om:
              </p>
              <ul className="text-sm text-purple-800 space-y-1 text-left ml-6">
                <li>• Kontobekreftelse og sikkerhet</li>
                <li>• Abonnements- og betalingsinformasjon</li>
                <li>• Viktige systemoppdateringer</li>
              </ul>
            </div>

            <button
              onClick={handleResubscribe}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors mb-3"
            >
              <Settings className="inline w-4 h-4 mr-2" />
              Meld deg på igjen
            </button>

            <button
              onClick={() => {
                onNavigate('home');
              }}
              className="w-full text-gray-600 py-3 rounded-lg font-medium hover:text-gray-900 transition-colors"
            >
              Gå til Talentfunn
            </button>
          </div>
        )}

        {/* Error state */}
        {(status === 'error' || status === 'invalid') && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {status === 'invalid' ? 'Ugyldig link' : 'Noe gikk galt'}
            </h1>
            <p className="text-gray-600 mb-8">
              {errorMessage}
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                Hvis du ønsker å melde deg av markedsførings-e-poster, kan du gjøre det i innstillingene dine:
              </p>
            </div>

            <button
              onClick={handleResubscribe}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors mb-3"
            >
              <Settings className="inline w-4 h-4 mr-2" />
              Gå til innstillinger
            </button>

            <button
              onClick={() => {
                onNavigate('home');
              }}
              className="w-full text-gray-600 py-3 rounded-lg font-medium hover:text-gray-900 transition-colors"
            >
              Gå til Talentfunn
            </button>
          </div>
        )}
    </div>
  );

  if (isAndroid) {
    return (
      <div
        className="xl:hidden fixed left-0 right-0 bottom-0 flex flex-col overflow-hidden bg-white z-[39]"
        style={{ top: headerHeight }}
      >
        <PageShell className="flex-1 min-h-0 overflow-y-auto ios-scroll pt-6 px-4">
          <div className="mx-auto max-w-md">
            {cardContent}
          </div>
        </PageShell>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-white xl:to-pink-50 flex items-center justify-center p-4">
      {cardContent}
    </div>
  );
}
